import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOverdueExternalReports, reportOverdue } from '../lib/incidents';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useModules } from '../components/RoleGuard';
import { getTodayVisits, getMissedVisits, getVisitsByDateRange, parseVisits, visitClientName, visitCaregiverName, visitActualHours } from '../lib/axiscare';
import { certificationStatus } from '../lib/training';
import { isClosedClient, isReceivingService, isInService } from '../lib/serviceModel';
import BirthdayRibbon from '../components/BirthdayRibbon';
import { format, differenceInDays } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';
import { colorForCount } from '../lib/countyMapUtil';
import { loadApprovedCounties } from '../lib/serviceArea';
import countyGeo from '../lib/gaCountyGeo.json';

const PA_WATCH_DAYS = 60; // matches PriorAuth.js's own "actionable" window
const WEEKS_PER_MONTH = 4.345;

// ── Shared shells ─────────────────────────────────────────────────────────────

// `tone` washes the whole card in a semantic tint ('alert' = warn, 'crit' =
// danger) so a card that needs action reads as urgent before you reach its
// numbers. The body is a flex column so a footer can pin to the bottom of a
// fixed-height card.
const Card = ({ title, titleRoute, badge, badgeTone = 'danger', right, children, style, tone }) => {
  const navigate = useNavigate();
  const badgeStyle = { copper: { bg: 'var(--copper-bg)', color: 'var(--copper)' }, danger: { bg: 'var(--danger-bg)', color: 'var(--danger)' } }[badgeTone];
  return (
    <div className={`card${tone ? ` card-${tone}` : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, ...style }}>
      <div className="card-header" style={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 6 }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: titleRoute ? 'pointer' : 'default' }} onClick={() => titleRoute && navigate(titleRoute)}>
          {title}
          {badge != null && badge > 0 && <span style={{ fontSize: 10, fontWeight: 500, padding: '1px 7px', borderRadius: 20, background: badgeStyle.bg, color: badgeStyle.color }}>{badge}</span>}
        </div>
        {right}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</div>
    </div>
  );
};

const Pill = ({ label, tone = 'neutral', minWidth }) => {
  const t = { danger: ['var(--danger-bg)', 'var(--danger)'], warn: ['var(--warn-bg)', 'var(--warn)'], copper: ['var(--copper-bg)', 'var(--copper)'], success: ['var(--success-bg)', 'var(--success)'], info: ['var(--info-bg)', 'var(--info)'], neutral: ['var(--bg2)', 'var(--text3)'] }[tone];
  return <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 9px', borderRadius: 20, background: t[0], color: t[1], flexShrink: 0, whiteSpace: 'nowrap', minWidth, textAlign: 'center' }}>{label}</span>;
};

// Small rounded-square icon tile — used for Action Required rows and the
// Personnel Snapshot cells. tone drives color; 'neutral' = no urgency.
const IconTile = ({ tone = 'neutral', size = 26, iconSize = 14, children }) => {
  const t = { danger: ['var(--danger-bg)', 'var(--danger)'], warn: ['var(--warn-bg)', 'var(--warn)'], copper: ['var(--copper-bg)', 'var(--copper)'], neutral: ['var(--bg2)', 'var(--text3)'] }[tone];
  return (
    <span style={{ width: size, height: size, borderRadius: size > 30 ? 11 : 7, background: t[0], color: t[1], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={iconSize} height={iconSize} fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">{children}</svg>
    </span>
  );
};

const DOC_ICON = <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>;
const LEAD_ICON = <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>;

// Pure-CSS conic-gradient donut with a center hole showing the total
const Donut = ({ segments, size = 112, thickness = 20, centerLabel }) => {
  const total = segments.reduce((s, x) => s + x.count, 0) || 1;
  let acc = 0;
  const stops = segments.filter(s => s.count > 0).map(s => {
    const start = (acc / total) * 360; acc += s.count;
    const end = (acc / total) * 360;
    return `${s.color} ${start}deg ${end}deg`;
  }).join(', ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, width: '100%' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: '50%', background: stops ? `conic-gradient(${stops})` : 'var(--bg2)' }}>
        <div style={{ position: 'absolute', inset: thickness, borderRadius: '50%', background: 'var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--text)', lineHeight: 1 }}>{total}</div>
          {centerLabel && <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{centerLabel}</div>}
        </div>
      </div>
      <div style={{ width: 210, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 11.5, padding: '3px 6px', borderRadius: 7 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--text2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
            <span style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13 }}>{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Deck primitives ────────────────────────────────────────────────────────
// Sub-stat tile: a headline figure broken into its parts, each a filter into
// the page behind it. Fills are the semantic *-bg tokens — no new hue.
const Tile = ({ label, value, sub, tone = 'mute', onClick }) => {
  const t = tone === 'mute'
    ? { bg: 'var(--bg2)', fg: 'var(--text3)', bd: 'var(--border)' }
    : { bg: `var(--${tone}-bg)`, fg: `var(--${tone})`, bd: `var(--${tone}-border)` };
  return (
    <div onClick={onClick} style={{ background: t.bg, border: `0.5px solid ${t.bd}`, borderRadius: 8, padding: '7px 9px', cursor: onClick ? 'pointer' : 'default', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, lineHeight: 1, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {sub && <span style={{ fontSize: 9.5, fontWeight: 600, color: t.fg, opacity: 0.8 }}>{sub}</span>}
      </div>
      <div style={{ fontSize: 9.5, fontWeight: 500, marginTop: 3, color: t.fg, letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  );
};
const Tiles = ({ cols = 3, style, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 6, flexShrink: 0, ...style }}>{children}</div>
);

// Semicircle gauge — reserved for a *rate*. Counts get a number; only a
// percentage earns an arc.
const Gauge = ({ pct, label, tone = 'warn' }) => {
  const ARC = 128.8;                                  // pi * r, r = 41
  const v = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{ position: 'relative', width: 106, height: 66, flexShrink: 0, textAlign: 'center' }}>
      <svg width="106" height="62" viewBox="0 0 106 62" role="img" aria-label={`${label} ${v.toFixed(0)} percent`}>
        <path d="M12 56 A41 41 0 0 1 94 56" fill="none" stroke="var(--bg2)" strokeWidth="12" strokeLinecap="round" />
        <path d="M12 56 A41 41 0 0 1 94 56" fill="none" stroke={`var(--${tone})`} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(v / 100) * ARC} ${ARC}`} />
      </svg>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 34, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{v.toFixed(0)}%</div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 54, fontSize: 8, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  );
};

// Big figure with a two-line caption.
const Fig = ({ value, caption, tone }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0, flexShrink: 0 }}>
    <span style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, lineHeight: 1, color: tone ? `var(--${tone})` : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    <span style={{ fontSize: 10.5, color: 'var(--text2)', fontWeight: 500, lineHeight: 1.25 }}>{caption}</span>
  </div>
);

// Compact list line — 31px, hairline-separated, so five fit a fixed card.
// The first line in a list drops its top rule via .dash-lines in index.css.
const Line = ({ onClick, lead, title, sub, tail }) => (
  <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', borderTop: '0.5px solid var(--border)', minHeight: 31, cursor: onClick ? 'pointer' : 'default' }}>
    {lead}
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
      {sub && <span style={{ display: 'block', fontSize: 10, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>}
    </span>
    {tail}
  </div>
);
const Lines = ({ children }) => <div className="dash-lines" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', padding: '0 14px' }}>{children}</div>;
const Ref = ({ children }) => <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text3)', marginLeft: 5 }}>{children}</span>;
const Dot = ({ color }) => <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, margin: '0 6px' }} />;
const Clock = ({ children }) => <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text2)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{children}</span>;

// Card footer: a note on the left, one link on the right. Lets a card stay
// short instead of trying to be the list.
const Foot = ({ note, link, to }) => {
  const navigate = useNavigate();
  return (
    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 14px', borderTop: '0.5px solid var(--border)', fontSize: 10.5, color: 'var(--text3)', marginTop: 'auto' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note}</span>
      {link && <span onClick={() => navigate(to)} style={{ color: 'var(--accent2)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>{link} &rarr;</span>}
    </div>
  );
};

// A resolved empty state reads better than a blank panel.
const AllSet = ({ title, sub }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 14px', textAlign: 'center' }}>
    <svg width="24" height="24" fill="none" stroke="var(--success)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text2)' }}>{title}</div>
    {sub && <div style={{ fontSize: 10, color: 'var(--text3)', maxWidth: '24ch' }}>{sub}</div>}
  </div>
);
const Loading = () => <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="loading-spinner" /></div>;

// Per-card control — one card answers a different question without leaving
// the dashboard.
const Segmented = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: 2, background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 7, padding: 2, flexShrink: 0 }}>
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)} style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 5, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: value === o ? 'var(--accent)' : 'transparent', color: value === o ? '#fff' : 'var(--text3)' }}>{o}</button>
    ))}
  </div>
);
// Where a card's numbers come from, so a coordinator knows which system to
// open to fix one.
const Source = ({ ax }) => <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: ax ? 'var(--copper)' : 'var(--text3)', flexShrink: 0 }}>{ax ? 'AxisCare' : 'CAERA'}</span>;

// Cropped to the served metro cluster — an approved county far from it (Bibb,
// down near Macon) is left out here; a thumbnail this small can't fit it
// without shrinking the part that actually matters. The footer names any
// approved county the crop leaves out. Full picture lives at
// /service-county-map.
const MAP_CROP = { x: 75, y: 28, w: 362, h: 302 };
const inCrop = (c) => c.cx >= MAP_CROP.x && c.cx <= MAP_CROP.x + MAP_CROP.w && c.cy >= MAP_CROP.y && c.cy <= MAP_CROP.y + MAP_CROP.h;

// `approved` comes from Settings → Approved Counties (lib/serviceArea.js).
const CountyMiniMap = ({ byCounty, approved = [] }) => {
  const approvedSet = new Set(approved.map(c => c.toLowerCase()));
  const maxV = Math.max(1, ...Object.values(byCounty));
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`${MAP_CROP.x} ${MAP_CROP.y} ${MAP_CROP.w} ${MAP_CROP.h}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        {countyGeo.counties.map(c => (
          <path key={c.name} d={c.d} fill={colorForCount(byCounty[c.name] || 0, maxV)} stroke="var(--card-bg)" strokeWidth={1} />
        ))}
        {countyGeo.counties.filter(c => approvedSet.has(c.name.toLowerCase())).map(c => (
          <path key={`o-${c.name}`} d={c.d} fill="none" stroke="var(--success)" strokeWidth={2} strokeLinejoin="round" />
        ))}
      </svg>
      {countyGeo.counties.map(c => {
        const v = byCounty[c.name] || 0;
        if (v <= 0) return null;
        if (!inCrop(c)) return null;
        return (
          <div key={c.name} style={{
            position: 'absolute', left: `${((c.cx - MAP_CROP.x) / MAP_CROP.w) * 100}%`, top: `${((c.cy - MAP_CROP.y) / MAP_CROP.h) * 100}%`,
            transform: 'translate(-50%,-50%)', minWidth: 15, height: 15, padding: '0 3px', borderRadius: 999,
            background: 'var(--accent2)', color: '#fff', fontSize: 8.5, fontWeight: 700, border: '1.4px solid var(--card-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {v}
          </div>
        );
      })}
    </div>
  );
};

// ── Dashboard ──────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { hasHH } = useModules();

  const [stats, setStats] = useState({ total: 0, totalLastMonth: 0, active: 0, pipeline: 0, paExpiring30: 0, paExpired: 0, needsAssessment: 0, totalLeads: 0, newLeads: 0, eligYes: 0, eligNo: 0, eligPending: 0, eligRecheckDue: 0, newAdmissionsThisWeek: 0, authorizedWeeklyHours: 0, authorizedHoursReliable: false });
  const [countyStats, setCountyStats] = useState({ totalActive: 0, countiesWithActive: 0, approvedWithActive: 0, approved: [], top: [], byCounty: {}, outside: [], noCounty: [], unrecognised: [] });
  // loadCRMData and loadOpsData run in parallel and each contribute their own
  // slice of Action Required items — kept in separate state (not one array
  // with functional setState merges) so neither fetch's completion order can
  // wholesale-overwrite the other's contribution.
  const [crmActionItems, setCrmActionItems] = useState([]);
  const [opsActionItems, setOpsActionItems] = useState([]);
  const actionItems = useMemo(() => [...crmActionItems, ...opsActionItems], [crmActionItems, opsActionItems]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [todayVisits, setTodayVisits] = useState([]);
  const [monthVisits, setMonthVisits] = useState([]);
  const [missedVisits, setMissedVisits] = useState([]);
  const [reportOverdueCount, setReportOverdueCount] = useState(0);
  useEffect(() => { getOverdueExternalReports().then(({ data }) => setReportOverdueCount((data || []).filter(reportOverdue).length)); }, []);
  const [axisLoading, setAxisLoading] = useState(true);
  const [axisError, setAxisError] = useState(null);
  const [hoursPeriod, setHoursPeriod] = useState('Month');
  // Supervisory visit cadence (Policy A-3.7) — the same rows and thresholds
  // SupervisorVisits.js uses, summarised for the card.
  const [supervision, setSupervision] = useState({ overdue: 0, dueWeek: 0, doneMonth: 0, rows: [] });

  useEffect(() => {
    loadCRMData();
    loadOpsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RequireAuth only waits on the auth session, not the profile row, so on
  // first mount `hasHH` can still be the 'staff' fallback — depend on it so
  // the AxisCare load fires once the real modules resolve.
  useEffect(() => {
    if (hasHH) loadAxisCareData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHH]);

  const loadCRMData = async () => {
    const [clientsRes, activityRes, tasksRes, leadsRes, leadActivityRes, approvedRes] = await Promise.all([
      supabase.from('clients').select('id, first_name, last_name, client_id, current_status, county, pa_expiry_date, dma_6a_expiry_date, pa_skilled_hours_week, pa_pss_hours_week, nurse_assessment_complete, medicaid_eligible, eligibility_recheck_date, created_at'),
      supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(12),
      supabase.from('tasks').select('*').neq('status', 'Done'),
      supabase.from('leads').select('id, lead_id, first_name, last_name, current_status, created_at, last_activity_date').not('current_status', 'in', '("Not Interested","Unreachable","Duplicate","Deceased","Ineligible","Moved / Outside Service Area","Chose Another Provider","Other","Converted")'),
      supabase.from('activity_log').select('*').not('lead_id', 'is', null).order('created_at', { ascending: false }).limit(8),
      loadApprovedCounties(),
    ]);
    const approvedCounties = approvedRes.data || [];

    const clients = clientsRes.data || [];
    const tasks = tasksRes.data || [];
    const leads = leadsRes.data || [];
    const leadActivity = leadActivityRes.data || [];
    const today = new Date();
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const newLeads = leads.filter(l => new Date(l.created_at) >= weekAgo).length;

    const patientActivity = activityRes.data || [];
    const mergedActivity = [...patientActivity, ...leadActivity]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 12);

    const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    const in60 = new Date(); in60.setDate(in60.getDate() + PA_WATCH_DAYS);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const activeClients = clients.filter(c => isReceivingService(c));
    const relevant = clients.filter(c => !isClosedClient(c));

    // Every active client is counted, and lands in exactly one place. The card
    // used to count only clients in an approved county and drop the rest
    // without a word, so it read 29 beside "33 clients on service" with
    // nothing to say where four children had gone. Now the approved counties
    // fill the map as before, and anyone outside them is counted underneath:
    // in a real county that is not on the approved list, with no county on
    // file, or with a county that matches no Georgia county (a misspelling).
    // Matching is case/whitespace-insensitive, as on the Service County Map.
    const countyByLower = new Map(countyGeo.counties.map(c => [c.name.toLowerCase(), c.name]));
    const approvedLower = new Set(approvedCounties.map(c => c.toLowerCase()));
    const activeByCounty = {};
    const outside = [], noCounty = [], unrecognised = [];
    const who = c => `${c.client_id || ''} ${[c.last_name, c.first_name].filter(Boolean).join(', ')}`.trim();
    activeClients.forEach(c => {
      const raw = (c.county || '').trim().toLowerCase();
      const canonical = countyByLower.get(raw);
      if (!raw) noCounty.push(who(c));
      else if (!canonical) unrecognised.push(`${who(c)} ("${(c.county || '').trim()}")`);
      else if (approvedLower.has(raw)) activeByCounty[canonical] = (activeByCounty[canonical] || 0) + 1;
      else outside.push({ county: canonical, who: who(c) });
    });
    const top = Object.entries(activeByCounty).sort((a, b) => b[1] - a[1]).slice(0, 3);
    const outsideCounties = [...new Set(outside.map(o => o.county))].sort();
    setCountyStats({
      totalActive: activeClients.length,
      // Counties anyone is served in, approved or not — the header and caption.
      countiesWithActive: Object.keys(activeByCounty).length + outsideCounties.length,
      // Approved counties with someone in them — what "Approved, none" is
      // measured against. Kept apart so the outside counties cannot shrink it.
      approvedWithActive: Object.keys(activeByCounty).length,
      approved: approvedCounties,
      top,
      byCounty: activeByCounty,
      outside, outsideCounties, noCounty, unrecognised,
    });
    const expiryOf = c => c.pa_expiry_date || c.dma_6a_expiry_date;

    const paExpired    = relevant.filter(c => { const d = expiryOf(c); return d && new Date(d) < today; });
    const paExpiring30 = relevant.filter(c => { const d = expiryOf(c); return d && new Date(d) >= today && new Date(d) <= in30; });
    // ≤60-day "actionable" window — matches PriorAuth.js's own threshold
    const paWatchList = relevant
      .filter(c => { const d = expiryOf(c); return d && new Date(d) < in60; })
      .map(c => {
        const d = expiryOf(c); const days = differenceInDays(parseLocalDate(d), today);
        return { id: c.id, name: c.last_name ? `${c.last_name}, ${c.first_name}` : `${c.first_name} ${c.last_name}`, ref: c.client_id, issue: days < 0 ? 'PA expired' : 'PA expiring soon', issueColor: 'var(--danger)', route: `/clients/${c.id}`, category: 'compliance', days };
      });

    const needsAssess = clients.filter(c => !c.nurse_assessment_complete && c.current_status === 'Nursing Assessment Pending');
    const overdueTasks = tasks.filter(t => t.due_date && differenceInDays(parseLocalDate(t.due_date), today) < 0);

    // eligPending is deliberately the "everything else" bucket (not just
    // null/'Pending') so these three always sum to clients.length — a real
    // client had medicaid_eligible = 'Not Applicable', which used to match
    // none of the three and silently vanish from the Eligibility donut.
    const eligYes     = clients.filter(c => c.medicaid_eligible === 'Yes').length;
    const eligNo      = clients.filter(c => c.medicaid_eligible === 'No').length;
    const eligPending = clients.filter(c => c.medicaid_eligible !== 'Yes' && c.medicaid_eligible !== 'No').length;
    const eligRecheckDue = relevant.filter(c => c.eligibility_recheck_date && new Date(c.eligibility_recheck_date) <= today).length;

    const totalLastMonth = clients.filter(c => c.created_at && new Date(c.created_at) < monthStart).length;
    const newAdmissionsThisWeek = activeClients.filter(c => c.created_at && new Date(c.created_at) >= weekAgo).length;
    // Real weekly PA hours, summed — the only actual "authorized hours" figure
    // in the schema (HoursAuth.js's own bar uses a flat placeholder formula
    // instead). But this field is populated inconsistently in practice — verified
    // live: summing it across all active clients gave ~16 weekly hours total
    // against 1731 actual hours delivered in a month, which is nonsensical, not
    // an authorization ceiling. Only trust it when a real majority of active
    // clients actually have one of the two fields set; otherwise the Dashboard
    // shows delivered hours alone rather than a fabricated-looking percentage.
    const clientsWithPaHours = activeClients.filter(c => c.pa_skilled_hours_week || c.pa_pss_hours_week).length;
    const authorizedWeeklyHours = activeClients.reduce((s, c) => s + (c.pa_skilled_hours_week || 0) + (c.pa_pss_hours_week || 0), 0);
    const authorizedHoursReliable = activeClients.length > 0 && (clientsWithPaHours / activeClients.length) >= 0.5;

    setStats({
      total: clients.length, totalLastMonth, active: activeClients.length,
      pipeline: relevant.length - activeClients.length,
      paExpiring30: paExpiring30.length, paExpired: paExpired.length, needsAssessment: needsAssess.length,
      totalLeads: leads.length, newLeads, eligYes, eligNo, eligPending, eligRecheckDue, newAdmissionsThisWeek, authorizedWeeklyHours, authorizedHoursReliable,
    });

    // Action Required — tagged by category so tab counts and totals never disagree.
    // Grouping follows the spec's own split: compliance = PA/credentials/care
    // plans/assessments; docs = tasks/eligibility verification/supervisory;
    // leads = no-contact.
    const items = [...paWatchList];
    needsAssess.forEach(c => items.push({ id: c.id, name: c.last_name ? `${c.last_name}, ${c.first_name}` : `${c.first_name} ${c.last_name}`, ref: c.client_id, issue: 'Assessment pending', issueColor: 'var(--warn)', route: `/clients/${c.id}`, category: 'compliance' }));
    overdueTasks.forEach(t => items.push({ id: t.client_id || null, name: t.title, ref: t.client_ref || '', issue: 'Overdue task', issueColor: 'var(--danger)', route: '/tasks', category: 'docs' }));
    const eligPendingPipeline = clients.filter(c => (!c.medicaid_eligible || c.medicaid_eligible === 'Pending') && ['Nursing Assessment Pending', 'Nursing Assessment Completed', 'PPOT Request Pending', 'PPOT Request In Progress'].includes(c.current_status));
    eligPendingPipeline.forEach(c => items.push({ id: c.id, name: c.last_name ? `${c.last_name}, ${c.first_name}` : `${c.first_name} ${c.last_name}`, ref: c.client_id, issue: 'Eligibility unverified', issueColor: 'var(--warn)', route: `/clients/${c.id}`, category: 'docs' }));
    const staleLeads = leads.filter(l => !l.last_activity_date || new Date(l.last_activity_date) < weekAgo);
    staleLeads.forEach(l => items.push({ id: l.id, name: l.last_name ? `${l.last_name}, ${l.first_name}` : `${l.first_name} ${l.last_name}`, ref: l.lead_id, issue: 'No contact 7d+', issueColor: 'var(--copper)', route: `/leads/${l.id}`, category: 'leads' }));
    setCrmActionItems(items);

    setPipeline([
      { zone: 'Leads',           color: 'var(--zone-leads)', count: leads.length },
      { zone: 'Outreach',        color: 'var(--zone-marketing)', count: clients.filter(c => ['New Referral', 'Client Contacted', 'Response Pending', 'Waitlist'].includes(c.current_status)).length },
      { zone: 'Clinical Intake', color: 'var(--zone-processing)', count: clients.filter(c => ['Intake Form Submitted', 'Nursing Assessment Pending', 'Nursing Assessment Completed', 'PPOT Request Pending', 'PPOT Request In Progress'].includes(c.current_status)).length },
      { zone: 'Authorization',   color: 'var(--zone-approval)', count: clients.filter(c => ['Submitted (DMA6A)', 'Submitted (DMA80)'].includes(c.current_status)).length },
      { zone: 'Active / In Service', color: 'var(--zone-active)', count: clients.filter(c => isInService(c)).length },
      { zone: 'Inactive',        color: 'var(--zone-closed)', count: clients.filter(c => isClosedClient(c)).length },
    ]);

    setRecentActivity(mergedActivity);
  };

  // Supervisory visit cadence and staff credential expiry — same
  // tables/thresholds SupervisorVisits.js and TrainingCompliance.js use,
  // folded into the one Action Required triage list instead of a separate
  // card (the real design has no standalone hero here).
  const loadOpsData = async () => {
    const today = new Date();
    // Care-plan-review reminder removed 2026-08-02: it assumed a fixed
    // 60-day review interval, but the Nursing Care Plan/PPOT review has no
    // fixed interval — it's tied to the recertification period (Policy
    // B-1.1, GAPP Provider Manual §913.2). No reliable recertification-date
    // field exists yet to replace it with, so this is removed rather than
    // guess at another wrong assumption.
    // Supervision comes from supervision_status, the same per caregiver–client
    // cadence the Clinical supervision board shows. It only pairs open clients
    // with active staff, and a completed visit resets the pair's clock; the old
    // read of supervisor_visits.next_visit_due flagged every completed visit
    // as overdue once its date passed, and never checked whether the client
    // had been discharged.
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const [boardRes, doneRes, certsRes] = await Promise.all([
      supabase.from('supervision_status').select('client_id, client_ref, client_first, client_last, staff_first, staff_last, staff_full, kind, last_date, next_due, status, supervisor_visit_id'),
      supabase.from('supervisor_visits').select('id', { count: 'exact', head: true }).eq('status', 'complete').gte('visit_date', format(monthStart, 'yyyy-MM-dd')),
      supabase.from('staff_certifications').select('*, staff(full_name, first_name, last_name, status)').order('expiry_date'),
    ]);

    const board = (boardRes.data || [])
      .filter(r => r.next_due)
      .map(r => ({ ...r, days: differenceInDays(parseLocalDate(r.next_due), today) }))
      .sort((a, b) => a.days - b.days);
    const pairName = r => `${r.client_last}, ${r.client_first}`;
    const supervisorItems = board
      .filter(r => r.days <= 14 && !r.supervisor_visit_id)
      .map(r => ({ id: `${r.client_id}-${r.kind}-${r.staff_first || ''}`, name: pairName(r), ref: r.client_ref, issue: r.days < 0 ? 'Supervisor visit overdue' : 'Supervisor visit due soon', issueColor: r.days < 0 ? 'var(--danger)' : 'var(--warn)', route: '/hh/supervisor-visits', category: 'docs' }));

    const credItems = (certsRes.data || []).filter(cert => cert.staff?.status !== 'terminated').map(cert => {
      const s = certificationStatus(cert);
      if (s.key !== 'expired' && s.key !== 'expiring') return null;
      const cs = cert.staff;
      return { id: cert.id, name: cs ? (cs.last_name ? `${cs.last_name}, ${cs.first_name}` : cs.full_name) : '—', ref: cert.cert_label || cert.cert_type, issue: s.key === 'expired' ? 'Credential expired' : 'Credential expiring', issueColor: s.key === 'expired' ? 'var(--danger)' : 'var(--warn)', route: '/hr/training-compliance', category: 'compliance' };
    }).filter(Boolean);

    setOpsActionItems([...supervisorItems, ...credItems]);

    setSupervision({
      overdue: board.filter(r => r.days < 0).length,
      dueWeek: board.filter(r => r.days >= 0 && r.days <= 7).length,
      doneMonth: doneRes.count || 0,
      rows: board.slice(0, 3).map(r => ({
        name: pairName(r), ref: r.client_ref, days: r.days, last: r.last_date,
        who: r.kind === 'client' ? 'Client review' : (r.staff_last ? `${r.staff_last}, ${r.staff_first}` : r.staff_full),
      })),
    });
  };

  const loadAxisCareData = async () => {
    setAxisLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const [visitsRes, missedRes, monthRes] = await Promise.all([getTodayVisits(), getMissedVisits(), getVisitsByDateRange(monthStart, now.toISOString().split('T')[0])]);
      const visitsArr = parseVisits(visitsRes.data?.results?.visits);
      setTodayVisits(visitsArr.filter(v => !v.removed));
      if (Array.isArray(missedRes.data)) setMissedVisits(missedRes.data);
      setMonthVisits(parseVisits(monthRes.data?.results?.visits).filter(v => !v.removed));
    } catch { setAxisError('AxisCare data unavailable'); }
    setAxisLoading(false);
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  const clientDelta = stats.totalLastMonth > 0 ? Math.round(((stats.total - stats.totalLastMonth) / stats.totalLastMonth) * 100) : null;

  const hoursData = useMemo(() => {
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const periodVisits = hoursPeriod === 'Week' ? monthVisits.filter(v => new Date(v.scheduledStartDate || v.startDate) >= weekAgo) : monthVisits;
    const delivered = periodVisits.reduce((s, v) => s + visitActualHours(v), 0);
    if (!stats.authorizedHoursReliable) return { delivered: delivered.toFixed(0), authorized: null, pct: null };
    const authorized = hoursPeriod === 'Week' ? stats.authorizedWeeklyHours : stats.authorizedWeeklyHours * WEEKS_PER_MONTH;
    const pct = authorized > 0 ? Math.min(100, (delivered / authorized) * 100) : 0;
    return { delivered: delivered.toFixed(0), authorized: authorized.toFixed(0), pct };
  }, [monthVisits, hoursPeriod, stats.authorizedWeeklyHours, stats.authorizedHoursReliable]);

  const itemIcon = (it) => it.category === 'leads' ? LEAD_ICON : DOC_ICON;
  const itemTone = (it) => it.issueColor === 'var(--danger)' ? 'danger' : it.issueColor === 'var(--warn)' ? 'warn' : 'copper';

  // ── Derived splits, all from data already loaded ────────────────────────
  const now = new Date();
  const visitStart = v => { const t = v.scheduledStartDate || v.startDate; return t ? new Date(t) : null; };
  const unassignedToday = todayVisits.filter(v => !v.caregiver);
  const openOverdue = unassignedToday.filter(v => { const t = visitStart(v); return t && t < now; }).length;
  const openSoon = unassignedToday.filter(v => { const t = visitStart(v); return t && t >= now && (t - now) <= 4 * 3600e3; }).length;
  const openLater = Math.max(0, unassignedToday.length - openOverdue - openSoon);
  const missedRate = monthVisits.length > 0 ? (missedVisits.length / monthVisits.length) * 100 : 0;
  const todayDone = todayVisits.filter(v => v.clockIn && v.clockOut).length;
  const todayRunning = todayVisits.filter(v => v.clockIn && !v.clockOut).length;
  const todayToGo = todayVisits.length - todayDone - todayRunning;
  const zoneCount = z => (pipeline.find(p => p.zone === z) || {}).count || 0;
  const pipelineTotal = pipeline.reduce((sum, z) => sum + z.count, 0);

  // Action Required is the one queue. Danger first, then warn, then the rest;
  // the overdue-incident count joins it as a row rather than a separate chip.
  const rank = c => c === 'var(--danger)' ? 0 : c === 'var(--warn)' ? 1 : 2;
  const queue = [
    ...(reportOverdueCount > 0 ? [{ name: `${reportOverdueCount} incident${reportOverdueCount === 1 ? '' : 's'} past the 24h reporting deadline`, ref: '', issue: 'Report', issueColor: 'var(--danger)', route: '/hr/disciplinary-log', category: 'compliance' }] : []),
    ...actionItems,
  ].sort((a, b) => rank(a.issueColor) - rank(b.issueColor));
  const urgent = queue.filter(i => i.issueColor === 'var(--danger)').length;
  const byCat = cat => queue.filter(i => i.category === cat).length;
  const CAP = 5;
  const more = (n, cap = CAP) => n > cap ? `+${n - cap} more` : '';
  const kpiSub = (parts) => parts.filter(Boolean).join(' · ');

  return (
    <div>
      {/* ── Welcome band: greeting plus five KPIs, each with a sub-line. The
             two that need attention pick out in amber. ── */}
      <div className="dash-band">
        <div className="dash-band-id">
          <div className="dash-band-greet">{greeting}, {firstName}</div>
          <div className="dash-band-date">{format(new Date(), 'EEEE, MMMM d yyyy')} · CAERA · A Heart For Care</div>
        </div>
        <div className="dash-kpis">
          {[
            { v: stats.active, l: 'Clients on service', s: kpiSub([`of ${stats.total} on file`, clientDelta != null && `${clientDelta >= 0 ? '▲' : '▼'} ${Math.abs(clientDelta)}%`]), route: '/clients', up: clientDelta != null && clientDelta >= 0 },
            { v: pipelineTotal, l: 'In pipeline', s: kpiSub([`${zoneCount('Active / In Service')} active`, `${zoneCount('Clinical Intake')} in intake`]), route: '/pipeline' },
            hasHH && { v: axisError ? '—' : todayVisits.length, l: 'Visits today', s: axisError ? 'AxisCare unavailable' : kpiSub([`${todayDone} done`, `${todayRunning} running`, `${todayToGo} to go`]), route: '/hh/calendar' },
            hasHH && { v: axisError ? '—' : unassignedToday.length, l: 'Shifts open', s: axisError ? '' : (openOverdue > 0 ? `${openOverdue} overdue` : 'none overdue'), route: '/hh/calendar', hot: unassignedToday.length > 0 },
            { v: queue.length, l: 'Actions', s: urgent > 0 ? `${urgent} urgent` : 'none urgent', route: null, hot: urgent > 0 },
          ].filter(Boolean).map(k => (
            <div key={k.l} onClick={() => k.route && navigate(k.route)} className={`dash-kpi${k.route ? ' link' : ''}`}>
              <div className={`dash-kpi-v${k.hot ? ' hot' : ''}`}>{k.v}</div>
              <div className="dash-kpi-l">{k.l}</div>
              <div className={`dash-kpi-s${k.up ? ' up' : ''}`}>{k.s}</div>
            </div>
          ))}
        </div>
        <BirthdayRibbon />
      </div>

      <div className="dash-deck">

        {/* ── Row 1: Clients ── */}

        <Card title="Client Pipeline" titleRoute="/pipeline" right={<Source />}>
          <div style={{ padding: '10px 14px 12px', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center' }}>
            <Donut segments={pipeline.map(z => ({ label: z.zone, count: z.count, color: z.color }))} centerLabel="pipeline" size={96} thickness={17} />
          </div>
          <Foot note={`${stats.newAdmissionsThisWeek} admitted this week`} link="Open pipeline" to="/pipeline" />
        </Card>

        {hasHH && (
          <Card title="Service Counties" titleRoute="/service-county-map" right={<span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{countyStats.countiesWithActive} counties</span>}>
            <div style={{ padding: '6px 14px 10px', flex: 1, minHeight: 0, display: 'flex', gap: 12, alignItems: 'stretch' }}>
              <div style={{ flex: 1, minWidth: 0, borderRadius: 8, overflow: 'hidden', background: 'var(--bg2)' }}>
                <CountyMiniMap byCounty={countyStats.byCounty} approved={countyStats.approved} />
              </div>
              {/* Top-aligned, and scrolls if it must. Centred, it spilled past
                  both edges once the outside-area rows were added, and the card
                  clipped the headline figure off the top (2026-10-20). */}
              <div style={{ width: 120, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 3, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                <div style={{ marginBottom: 4 }}><Fig value={countyStats.totalActive} caption={<>across<br />{countyStats.countiesWithActive} counties</>} /></div>
                {countyStats.top.map(([name, count]) => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: colorForCount(count, Math.max(1, ...Object.values(countyStats.byCounty))), flexShrink: 0 }} />
                    <span style={{ color: 'var(--text2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{count}</b>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: 'var(--text3)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--bg3)', border: '0.5px solid var(--success)', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>Approved, none</span>
                  <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{Math.max(0, countyStats.approved.length - countyStats.approvedWithActive)}</b>
                </div>
                {/* Everyone the map does not hold. Quiet on purpose — grey, no
                    warning colour — and only shown when there is someone to
                    count. Hover names them. */}
                {(countyStats.outside?.length > 0 || countyStats.noCounty?.length > 0 || countyStats.unrecognised?.length > 0) && (
                  <div style={{ borderTop: '0.5px solid var(--border)', marginTop: 4, paddingTop: 5, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, fontSize: 11, color: 'var(--text3)' }}>
                    {countyStats.outside.length > 0 && (
                      <div title={countyStats.outside.map(o => `${o.who} — ${o.county}`).join('\n')} style={{ cursor: 'default' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', border: '1px dashed var(--border2)', flexShrink: 0 }} />
                          <span style={{ flex: 1, minWidth: 0 }}>Outside approved area</span>
                          <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{countyStats.outside.length}</b>
                        </div>
                        <div style={{ paddingLeft: 15, fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{countyStats.outsideCounties.join(', ')}</div>
                      </div>
                    )}
                    {countyStats.noCounty.length > 0 && (
                      <div title={countyStats.noCounty.join('\n')} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'default' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', border: '1px dashed var(--border2)', flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0 }}>No county recorded</span>
                        <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{countyStats.noCounty.length}</b>
                      </div>
                    )}
                    {countyStats.unrecognised.length > 0 && (
                      <div title={countyStats.unrecognised.join('\n')} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'default' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', border: '1px dashed var(--border2)', flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0 }}>County not recognised</span>
                        <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{countyStats.unrecognised.length}</b>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Foot note={(() => {
              const off = countyGeo.counties.filter(c => !inCrop(c) && countyStats.approved.some(a => a.toLowerCase() === c.name.toLowerCase())).map(c => c.name);
              return off.length ? `${off.join(', ')} on the full map` : '';
            })()} link="Open county map" to="/service-county-map" />
          </Card>
        )}

        <Card tone={urgent > 0 ? 'crit' : undefined} title="Action Required" badge={queue.length}>
          {queue.length === 0
            ? <AllSet title="All clear" sub="Nothing in the queue needs a decision right now." />
            : (
              <Lines>
                {queue.slice(0, CAP).map((item, i) => (
                  <Line key={i} onClick={() => item.route && navigate(item.route)}
                    lead={<IconTile tone={itemTone(item)} size={24} iconSize={13}>{itemIcon(item)}</IconTile>}
                    title={<>{item.name}{item.ref && <Ref>{item.ref}</Ref>}</>}
                    sub={item.category === 'leads' ? 'Lead · no contact 7 days' : item.category === 'compliance' ? 'Compliance' : 'Documentation'}
                    tail={<Pill label={item.issue} tone={itemTone(item)} />} />
                ))}
              </Lines>
            )}
          <Foot note={kpiSub([more(queue.length), `${byCat('compliance')} compliance`, `${byCat('docs')} docs`, `${byCat('leads')} leads`])} link="Open queue" to="/tasks" />
        </Card>

        {/* ── Row 2: Today's care delivery (AxisCare) ── */}

        {hasHH && (
          <Card title="Today's Visits" titleRoute="/hh/calendar" right={<Source ax />}>
            <div style={{ padding: '8px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
              <Fig value={axisLoading ? '…' : todayVisits.length} caption={<>scheduled<br />today</>} />
              <div style={{ flex: 1, maxWidth: 150 }}>
                <div style={{ display: 'flex', height: 6, borderRadius: 20, overflow: 'hidden', gap: 2, background: 'var(--bg2)' }}>
                  {todayVisits.length > 0 && <>
                    <span style={{ width: `${(todayDone / todayVisits.length) * 100}%`, background: 'var(--success)', borderRadius: 2 }} />
                    <span style={{ width: `${(todayRunning / todayVisits.length) * 100}%`, background: 'var(--info)', borderRadius: 2 }} />
                    <span style={{ width: `${(todayToGo / todayVisits.length) * 100}%`, background: 'var(--border2)', borderRadius: 2 }} />
                  </>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                  <span>{todayDone} done · {todayRunning} running</span><b style={{ color: 'var(--text2)', fontWeight: 600 }}>{todayToGo} to go</b>
                </div>
              </div>
            </div>
            {axisLoading ? <Loading /> : todayVisits.length === 0 ? <AllSet title="No visits today" /> : (
              <Lines>
                {todayVisits.slice(0, 3).map((v, i) => {
                  const state = v.clockIn && v.clockOut ? 'completed' : v.clockIn ? 'inProgress' : 'scheduled';
                  const t = visitStart(v);
                  return (
                    <Line key={i} onClick={() => navigate('/hh/calendar?tab=history')}
                      lead={<Dot color={{ completed: 'var(--success)', inProgress: 'var(--info)', scheduled: 'var(--border2)' }[state]} />}
                      title={visitClientName(v)} sub={visitCaregiverName(v)}
                      tail={<><Clock>{t ? format(t, 'HH:mm') : '—'}</Clock><Pill label={{ completed: 'Done', inProgress: 'Running', scheduled: 'Next' }[state]} tone={{ completed: 'success', inProgress: 'info', scheduled: 'neutral' }[state]} /></>} />
                  );
                })}
              </Lines>
            )}
            <Foot note={more(todayVisits.length, 3)} link="Open calendar" to="/hh/calendar" />
          </Card>
        )}

        {hasHH && (
          <Card tone={openOverdue > 0 ? 'crit' : unassignedToday.length > 0 ? 'alert' : undefined} title="Open Visits" badge={unassignedToday.length} badgeTone="copper" titleRoute="/hh/calendar" right={<Source ax />}>
            <div style={{ padding: '8px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
              <Fig value={axisLoading ? '…' : unassignedToday.length} caption={<>shifts need<br />a caregiver</>} tone={unassignedToday.length > 0 ? 'warn' : undefined} />
              <Tiles cols={3} style={{ flex: 1, maxWidth: 210 }}>
                <Tile label="Overdue" value={openOverdue} tone={openOverdue > 0 ? 'danger' : 'mute'} onClick={() => navigate('/hh/calendar')} />
                <Tile label="Within 4 hr" value={openSoon} tone={openSoon > 0 ? 'warn' : 'mute'} onClick={() => navigate('/hh/calendar')} />
                <Tile label="Upcoming" value={openLater} tone="mute" onClick={() => navigate('/hh/calendar')} />
              </Tiles>
            </div>
            {axisLoading ? <Loading /> : unassignedToday.length === 0 ? <AllSet title="You're all set" sub="Every shift today has a caregiver assigned." /> : (
              <Lines>
                {unassignedToday.slice(0, 3).map((v, i) => {
                  const t = visitStart(v); const late = t && t < now;
                  return (
                    <Line key={i} onClick={() => navigate('/hh/calendar')}
                      lead={<IconTile tone={late ? 'danger' : 'warn'} size={24} iconSize={13}>{late ? <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></> : <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>}</IconTile>}
                      title={visitClientName(v)} sub={`${t ? format(t, 'HH:mm') : '—'} · unassigned`}
                      tail={<Pill label={late ? 'Overdue' : 'Open'} tone={late ? 'danger' : 'warn'} />} />
                  );
                })}
              </Lines>
            )}
            <Foot note={more(unassignedToday.length, 3)} link="Assign caregivers" to="/hh/calendar" />
          </Card>
        )}

        {hasHH && (
          <Card tone={missedVisits.length > 0 ? 'alert' : undefined} title="EVV Clock Exceptions" titleRoute="/hh/calendar?tab=history" right={<Source ax />}>
            <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Fig value={axisLoading ? '…' : missedVisits.length} caption={<>missed /<br />unverified</>} />
                <Fig value={axisLoading ? '…' : monthVisits.length} caption={<>visits<br />this month</>} />
              </div>
              <Gauge pct={missedRate} label="missed rate" tone={missedRate > 10 ? 'danger' : 'warn'} />
            </div>
            {axisLoading ? <Loading /> : missedVisits.length === 0 ? <AllSet title="No clock exceptions" sub="Every visit this month clocked in and out cleanly." /> : (
              <Lines>
                {missedVisits.slice(0, 2).map((v, i) => (
                  <Line key={i} onClick={() => navigate('/hh/calendar?tab=history')}
                    lead={<IconTile tone="danger" size={24} iconSize={13}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></IconTile>}
                    title={visitClientName(v)} sub={visitCaregiverName(v)} tail={<Pill label="Resolve" tone="danger" />} />
                ))}
              </Lines>
            )}
            <Foot note={more(missedVisits.length, 2)} link="Open verification" to="/hh/calendar?tab=history" />
          </Card>
        )}

        {/* ── Row 3: Authorization, supervision, activity ── */}

        {hasHH && (
          <Card title="Authorization" titleRoute="/prior-auth" right={<Segmented options={['Month', 'Week']} value={hoursPeriod} onChange={setHoursPeriod} />}>
            <div style={{ padding: '8px 14px 0', flexShrink: 0 }}>
              {hoursData.authorized != null ? (
                <>
                  <Fig value={hoursData.delivered} caption={<>of {hoursData.authorized} h<br />authorised</>} />
                  <div style={{ height: 6, borderRadius: 20, background: 'var(--bg2)', overflow: 'hidden', marginTop: 8 }}>
                    <div style={{ width: `${hoursData.pct}%`, height: '100%', borderRadius: 20, background: 'var(--copper)' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    <span>Hours delivered · {hoursPeriod.toLowerCase()}</span><b style={{ color: 'var(--text2)', fontWeight: 600 }}>{hoursData.pct.toFixed(0)}%</b>
                  </div>
                </>
              ) : (
                <>
                  <Fig value={hoursData.delivered} caption={<>hours delivered<br />this {hoursPeriod.toLowerCase()}</>} />
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>Not enough clients have weekly PA hours on file to estimate an authorised total.</div>
                </>
              )}
            </div>
            <div style={{ padding: '10px 14px 8px' }}>
              <Tiles cols={3}>
                <Tile label="PA expired" value={stats.paExpired} tone={stats.paExpired > 0 ? 'danger' : 'mute'} onClick={() => navigate('/prior-auth')} />
                <Tile label="Expiring ≤30 d" value={stats.paExpiring30} tone={stats.paExpiring30 > 0 ? 'warn' : 'mute'} onClick={() => navigate('/prior-auth')} />
                <Tile label="Recheck due" value={stats.eligRecheckDue} tone={stats.eligRecheckDue > 0 ? 'copper' : 'mute'} onClick={() => navigate('/clients')} />
              </Tiles>
            </div>
            <Foot note={`${stats.eligYes} eligible · ${stats.eligPending} pending check`} link="Open prior auth" to="/prior-auth" />
          </Card>
        )}

        {hasHH && (
          <Card tone={supervision.overdue > 0 ? 'alert' : undefined} title="Supervisory Visits" badge={supervision.overdue} titleRoute="/hh/supervisor-visits" right={<span style={{ fontSize: 10.5, color: 'var(--text3)' }}>This week</span>}>
            <div style={{ padding: '8px 14px 6px' }}>
              <Tiles cols={3}>
                <Tile label="Overdue" value={supervision.overdue} tone={supervision.overdue > 0 ? 'danger' : 'mute'} onClick={() => navigate('/hh/supervisor-visits')} />
                <Tile label="Due this week" value={supervision.dueWeek} tone={supervision.dueWeek > 0 ? 'warn' : 'mute'} onClick={() => navigate('/hh/supervisor-visits')} />
                <Tile label="Done this month" value={supervision.doneMonth} tone={supervision.doneMonth > 0 ? 'success' : 'mute'} onClick={() => navigate('/hh/supervisor-visits')} />
              </Tiles>
            </div>
            {supervision.rows.length === 0 ? <AllSet title="Cadence is clear" sub="No supervisory visits are scheduled or overdue." /> : (
              <Lines>
                {supervision.rows.map((r, i) => (
                  <Line key={i} onClick={() => navigate('/hh/supervisor-visits')}
                    lead={<IconTile tone={r.days < 0 ? 'danger' : r.days <= 7 ? 'warn' : 'neutral'} size={24} iconSize={13}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></IconTile>}
                    title={<>{r.name}{r.ref && <Ref>{r.ref}</Ref>}</>}
                    sub={[r.who, r.last ? `last ${format(parseLocalDate(r.last), 'd MMM')}` : 'no visit on record'].filter(Boolean).join(' · ')}
                    tail={<Pill label={r.days < 0 ? `${r.days} d` : r.days === 0 ? 'Today' : `${r.days} d`} tone={r.days < 0 ? 'danger' : r.days <= 7 ? 'warn' : 'neutral'} />} />
                ))}
              </Lines>
            )}
            <Foot note="A-3.7 biweekly cadence" link="Open supervisory visits" to="/hh/supervisor-visits" />
          </Card>
        )}

        <Card title="Recent Activity" titleRoute="/activity-logs">
          {recentActivity.length === 0 ? <AllSet title="No recent activity" /> : (
            <Lines>
              {recentActivity.slice(0, CAP).map((a, i) => {
                const isLead = !!a.lead_id;
                const route = isLead ? `/leads/${a.lead_id}` : (a.client_id ? `/clients/${a.client_id}` : null);
                const label = a.new_status || a.activity_type || '';
                const dot = /missed|no.?contact/i.test(label) ? 'var(--copper)' : /complet|approved|signed/i.test(label) ? 'var(--success)' : 'var(--text3)';
                return (
                  <Line key={i} onClick={() => route && navigate(route)} lead={<Dot color={dot} />}
                    title={<>{label}{isLead && <span style={{ fontSize: 9, background: 'var(--bg2)', color: 'var(--text3)', padding: '1px 5px', borderRadius: 3, fontWeight: 600, marginLeft: 6 }}>Lead</span>}<Ref>{isLead ? a.lead_ref : a.client_ref}</Ref></>}
                    sub={a.performed_by_name}
                    tail={<Clock>{a.created_at ? format(new Date(a.created_at), 'HH:mm') : ''}</Clock>} />
                );
              })}
            </Lines>
          )}
          <Foot note={more(recentActivity.length)} link="Open activity logs" to="/activity-logs" />
        </Card>

      </div>
    </div>
  );
}
